import dayjs from 'dayjs';
import { Quill } from 'react-quill';
import { v4 as randomUUID } from 'uuid';

// @ts-ignore
export const localRandomUuid: string = randomUUID();

/**
 * Converts the snake case to title case
 * ex:
 * input : 'user_good'
 * output : 'User good'
 * @param {string} string
 * @returns {string}
 */
export const convertSnakeCaseToTitleCase = (string: string): string => {
  if (!string) {
    return '';
  }

  return string
    .replace(/_/g, ' ')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Converts the input number with commas
 * ex:
 * input : '12232'
 * output : '12,232'
 * @param {number} x
 * @returns {string}
 */
export const numberWithCommas = (x: any): string =>
  x && x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/**
 * coverts number to US Dollar
 * ex:
 * input : '12232'
 * output : '12,232'
 * @param {number} x
 * @returns {string}
 */
export const priceFormatter = (x: any): string => {
  return x || x === 0 || x === '0'
    ? `$${numberWithCommas(Number.parseFloat(`${x}`)?.toFixed(2))}`
    : x;
};

const antColorToHexMap: Record<string, string> = {
  green: '#52c41a',
  cyan: '#13c2c2',
  grey: '#bfbfbf',
  yellow: '#fadb14',
  red: '#ff4d4f',
  gold: '#faad14',
  magenta: '#eb2f96',
  purple: '#722ed1',
  blue: '#1677ff',
};

/**
 * Returns the appropriate color for the given status
 * @param {string} status
 * @returns {string}
 */
export const getStatusColor = (status: string): string => {
  const greenStatus = [
    'completed',
    'online',
    'success',
    'paid',
    'processed',
    'sent',
    'active',
    'published',
    'succeeded',
  ];
  const yellowStatus = ['approved', 'draft'];
  const redStatus = ['needs_attention', 'failed', 'fail', 'high'];
  const purpleStatus = ['todo'];
  const cyanStatus = ['in_progress', 'scheduled'];
  const blueStatus = ['working_on_now'];
  const magentaStatus = ['waiting_approval', 'waiting_to_launch'];
  const goldStatus = ['pending', 'paused', 'to_review', 'medium'];
  const greyStatus = ['cancelled', 'inactive', 'deleted'];

  let color = 'blue';

  if (greyStatus.find((el) => el === status)) {
    color = 'grey';
  }

  if (purpleStatus.find((el) => el === status)) {
    color = 'purple';
  }
  if (cyanStatus.find((el) => el === status)) {
    color = 'cyan';
  }
  if (blueStatus.find((el) => el === status)) {
    color = 'blue';
  }
  if (magentaStatus.find((el) => el === status)) {
    color = 'magenta';
  }
  if (goldStatus.find((el) => el === status)) {
    color = 'gold';
  }
  if (greenStatus.find((el) => el === status)) {
    color = 'green';
  }
  if (yellowStatus.find((el) => el === status)) {
    color = 'yellow';
  } else if (redStatus.find((el) => el === status)) {
    color = 'red';
  }
  return color;
};

/**
 * Returns the hex color code for the given status
 * @param {string} status
 * @returns {string}
 */
export const getStatusColorHex = (status: string): string => {
  const statusColor = getStatusColor(status);
  return antColorToHexMap[statusColor] || antColorToHexMap.blue;
};

export function getRequestStatusOptions(withDraft?: boolean) {
  return [
    ...(withDraft ? [{ label: 'Draft', value: 'draft' }] : []),
    { label: 'In preparation', value: 'new' },
    { label: 'Waiting Approval', value: 'waiting_approval' },
    { label: 'Needs attention', value: 'needs_attention' },
    { label: 'Approved', value: 'approved' },
    { label: 'In progress', value: 'in_progress' },
    { label: 'Completed', value: 'completed' },
    { label: 'Cancelled', value: 'cancelled' },
  ];
}

export function getRequestStatusLabel(value: string) {
  return getRequestStatusOptions(true).find((item) => item.value === value)
    ?.label;
}

export const parseDateToUSFormat = (date: any) => {
  if (!date) {
    return '-';
  }

  return dayjs(date).format('MM/DD/YYYY');
};

export const parseDateToMMMDD = (date: any, utc: boolean = false) => {
  if (!date) {
    return undefined;
  }

  const dateObj = utc ? dayjs.utc(date) : dayjs(date);
  const isSameYear = dateObj.isSame(dayjs(), 'year');

  const format = isSameYear ? 'MMM DD[th]' : 'MMM DD[th], YY';

  return dateObj.format(format);
};

export const parseDateTimeToUSFormat = (date: any) => {
  if (!date) {
    return '-';
  }

  return dayjs(date).format('MM/DD/YYYY HH:mm');
};

export const getMentionUuidFromNode = (markup: string): string[] => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(markup, 'text/html');
  const uuids: string[] = [];

  doc.querySelectorAll<HTMLSpanElement>('span.mention').forEach((item) => {
    if (item.innerText.trim() && item.dataset.id) {
      uuids.push(item.dataset.id as string);
    }
  });

  return uuids;
};

/**
 * Parses the comment
 * @param {string} comment
 * @returns {string}
 */
export const mentionCommentParser = (content: string): string => {
  const regex = /@\[(.*?)\]\(.*?\)/g;

  return content.replaceAll(regex, '<span class="mention">@$1</span>');
};

export const deltaToHtml = (deltaString: string): string => {
  if (typeof document === 'undefined') {
    return '';
  }

  let delta;
  try {
    delta = JSON.parse(deltaString);
  } catch {
    return deltaString;
  }

  if (!delta || typeof delta !== 'object' || !Array.isArray(delta.ops)) {
    return deltaString;
  }

  const container = document.createElement('div');
  const quill = new Quill(container);
  quill.setContents(delta);

  return quill.root.innerHTML;
};

/**
 * Check if the given input is valid UUID
 * @param {string} uuid
 * @returns {boolean}
 */
export const checkIfValidUUID = (uuid: string): boolean => {
  // Regular expression to check if string is a valid UUID
  const regexExp =
    /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi;

  return regexExp.test(uuid);
};

export const getUserInitials = (user?: { name: string }): string | null => {
  if (!user?.name || user.name.trim() === '') {
    return null;
  }

  const nameArr = user.name.trim().split(/\s+/);

  if (nameArr.length >= 2) {
    const firstNameInitial = nameArr[0].charAt(0).toUpperCase();
    const lastNameInitial = nameArr[nameArr.length - 1].charAt(0).toUpperCase();

    return `${firstNameInitial}${lastNameInitial}`;
  }

  if (nameArr.length === 1) {
    const name = nameArr[0].toUpperCase();
    if (name.length >= 2) {
      return name.substring(0, 2);
    }
    return `${name.charAt(0)}${name.charAt(0)}`;
  }

  return null;
};

export const getAvatar = (custom?: any) => {
  const user = custom;

  if (!user) {
    return null;
  }

  const nameArr = user?.name?.split(' ') ?? [];
  let name = '';
  const avatar = user?.avatar;

  if (avatar)
    return {
      type: 'img',
      value: avatar,
    };

  nameArr.map((val: string) => {
    name += val.charAt(0);
    return val;
  });
  return {
    type: 'text',
    value: name,
  };
};

/**
 * Return the query string value from the URL
 * @param {string} search
 * @param {string} key
 * @returns {string}
 */
export const getQueryValue = (search: string, key: string) => {
  const query = new URLSearchParams(search);
  return query.get(key);
};

export const urlify = (text = '') => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return text?.replace(urlRegex, (url) => {
    return '<a target="blank" href="' + url + '">' + url + '</a>';
  });
};

export const trimUrl = (url: string | undefined) => {
  if (!url) return null;

  if (url.slice(-1) === '/') {
    url = url.slice(0, -1);
  }

  return url;
};

export const convertArrToCommaSeparatedString = (array: any[]): string => {
  let finalString = '';

  array?.forEach((el: any, i: any) => {
    finalString += `${el}${i === array?.length - 1 ? '' : ', '}`;
  });

  return finalString;
};

export const includesInObject = (value: string, object: any) => {
  if (!object || !value) {
    return false;
  }

  let hasSearch = false;

  Object.keys(object).forEach((key: string) => {
    if (typeof object[key] === 'string') {
      if (object[key]?.toLowerCase().includes(value.toLowerCase())) {
        hasSearch = true;
      }
    } else if (typeof object[key] === 'object') {
      return includesInObject(value, object[key]);
    }
  });

  return hasSearch;
};

export const getDateDifferenceInMinutes = (
  startDate: string,
  endDate = new Date(Date.now())
): number => {
  return dayjs(endDate).diff(startDate) / 60000;
};

export const convertMinToHrsMin = (minutes?: number | null): string => {
  if (!minutes) return '00:00';

  const value = minutes < 0 ? minutes * -1 : minutes;

  const h = Math.floor(value / 60);
  const m = value % 60;
  const hStr = h < 10 ? `0${h}` : h; // (or alternatively) h = String(h).padStart(2, '0')
  const mStr = m < 10 ? `0${m}` : m; // (or alternatively) m = String(m).padStart(2, '0')
  return `${minutes < 0 ? '-' : ''}${hStr}:${mStr}`;
};

export const convertMinToRoundedHours = (minutes?: number | null): string => {
  if (!minutes) return '0';
  const value = minutes < 0 ? minutes * -1 : minutes;
  const hours = Math.ceil(value / 60);
  return `${minutes < 0 ? '-' : ''}${hours}`;
};

export const convertMinToTotalHours = (minutes?: number | null) => {
  if (!minutes) return '00';

  const value = minutes < 0 ? minutes * -1 : minutes;
  const h = Math.floor(value / 60);
  const hStr = h < 10 ? `0${h}` : h;
  return `${minutes < 0 ? '-' : ''}${hStr}`;
};

export const convertMinToHHMM = (minutes?: number | null): string => {
  if (!minutes) return '00h00';

  const value = minutes < 0 ? minutes * -1 : minutes;

  const h = Math.floor(value / 60);
  const m = value % 60;
  const hStr = h < 10 ? `0${h}` : h; // (or alternatively) h = String(h).padStart(2, '0')
  const mStr = m < 10 ? `0${m}` : m; // (or alternatively) m = String(m).padStart(2, '0')
  return `${minutes < 0 ? '-' : ''}${hStr}h${mStr}`;
};

export const removeImage = (html: string | null, url: string) => {
  if (!html) {
    return html;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  doc.querySelector(`img[src="${url}"]`)?.parentElement?.remove();

  return doc.body.innerHTML;
};

export const checkExternalUrl = (urlString: string) => {
  const url = new URL(urlString);

  return window.location.hostname !== url.hostname;
};

export const getCompletePathUrl = (urlString: string) => {
  const url = new URL(urlString);

  return url.pathname + url.search;
};

export const formatBytes = (bytes: number, decimals = 2) => {
  if (!+bytes) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'Kb', 'Mb', 'Gb', 'Tb', 'Pb', 'Eb', 'Zb', 'Yb'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

export const getExtensionFromFileName = (name: string) => {
  return name.includes('.')
    ? name.split('.').reverse()[0].toLowerCase()
    : undefined;
};

export const formatMinutesToHoursAndMinutes = (
  totalMinutes: number
): string => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (minutes === 0) {
    return `${hours} hours`;
  }
  return `${hours}h${minutes.toString().padStart(2, '0')}min`;
};
